import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from '../api/axiosInstance';
import { Container, Row, Col, Pagination, Form, Spinner } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaRegClock, FaSearch } from "react-icons/fa";
import {
    useLocation,
    useNavigate,
} from "react-router-dom";

// 한 페이지에 출력될 데이터 수 (백엔드 설정과 동일)
const ITEMS_PER_PAGE = 12;

// API 엔드포인트 URL
const API_URL = "posts";

// CryptoNotice 메인 컴포넌트
const CryptoNotice = () => {
    // 1. URL 및 상태 관리
    const navigate = useNavigate();
    const location = useLocation();

    // URL에서 페이지와 검색어를 직접 파생 (Source of Truth)
    const searchParams = new URLSearchParams(location.search);
    const currentPage = parseInt(searchParams.get('page')) || 1;
    const searchTitle = searchParams.get('title') || "";

    const [postList, setPostList] = useState([]);
    const [titleInput, setTitleInput] = useState(searchTitle);
    const [totalPostCount, setTotalPostCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [flipKey, setFlipKey] = useState(0); // 페이지 전환 애니메이션을 위한 키

    // 백엔드 호출 함수
    const fetchPosts = useCallback(async (page, title) => {
        setLoading(true);
        // 페이지 전환 애니메이션 키 업데이트
        setFlipKey(prev => prev + 1);

        const boardId = 1; // 필수 파라미터
        let url = `${API_URL}?board_id=${boardId}&page=${page}`;
        if (title) {
            url += `&title=${encodeURIComponent(title)}`;
        }

        try {
            // 실제 n8n 웹훅 API 호출
            const response = await axiosInstance.get(url);
            // 응답 구조: response.data.data
            const apiData = response.data.data;

            if (apiData?.postList) {
                setPostList(apiData.postList);

                // postcount는 리스트의 첫 번째 항목에 전체 게시글 수가 있다고 가정
                const count = apiData.postList[0]?.json?.postcount || 0;
                setTotalPostCount(count);

            } else {
                setPostList([]);
                setTotalPostCount(0);
            }
        } catch (error) {
            // 에러 발생 시 처리
            console.error("Error fetching crypto notices:", error);
            setPostList([]);
            setTotalPostCount(0);
        }
        setLoading(false);
    }, []);

    // 초기 로딩 및 URL 변경 감지 시 데이터 로드
    useEffect(() => {
        fetchPosts(currentPage, searchTitle);
        setTitleInput(searchTitle);
    }, [location.search, fetchPosts, currentPage, searchTitle]);

    // 상세 보기 (새 창으로 열기)
    const handleCardClick = (post) => {
        if (post.content) {
            window.open(post.content, '_blank', 'noopener,noreferrer');
        }
    };

    // 페이지네이션 처리
    const totalPages = Math.ceil(totalPostCount / ITEMS_PER_PAGE);

    // 페이지 변경 핸들러는 URL만 업데이트
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            const newParams = new URLSearchParams(location.search);
            newParams.set('page', page);
            navigate({ search: newParams.toString() }, { replace: true });
        }
    };

    // 검색 폼 제출 처리 핸들러는 URL만 업데이트
    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const newParams = new URLSearchParams();

        // 검색 실행 시 1페이지로 이동
        newParams.set('page', 1);
        if (titleInput) {
            newParams.set('title', titleInput);
        }

        navigate({ search: newParams.toString() }, { replace: true });
    };

    // 카드 뒤집기 애니메이션 변형 정의
    const cardVariants = {
        hidden: { rotateY: -90, opacity: 0 },
        visible: { rotateY: 0, opacity: 1 },
        exit: { rotateY: 90, opacity: 0 }
    };

    const cardTransition = {
        type: "spring",
        stiffness: 100,
        damping: 10,
        duration: 0.5
    };

    return (
        <Container className="py-5 crypto-card-container">
            <h2 className="text-center mb-2 fw-bold" style={{ color: '#1e293b' }}>📰 가상화폐 실시간 뉴스 피드</h2>
            <p className="text-center text-secondary mb-4">해외 주요 크립토 미디어 및 속보를 실시간으로 받아봅니다.</p>

            {/* 검색 폼 */}
            <Form onSubmit={handleSearchSubmit} className="mb-4">
                <Row className="justify-content-center">
                    <Col xs={12} md={6} lg={4}>
                        <div className="crypto-search-wrapper">
                            <FaSearch className="crypto-search-icon" />
                            <Form.Control
                                type="text"
                                placeholder="뉴스 제목 검색"
                                value={titleInput}
                                onChange={(e) => setTitleInput(e.target.value)}
                                className="crypto-search-input"
                            />
                        </div>
                    </Col>
                </Row>
            </Form>

            {/* 로딩 인디케이터 */}
            {loading && (
                <div className="text-center my-5">
                    <Spinner animation="border" variant="primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-secondary">뉴스를 불러오는 중...</p>
                </div>
            )}

            {/* 카드 리스트 */}
            {!loading && postList.length > 0 && (
                <motion.div key={flipKey} className="crypto-news-grid-motion">
                    <Row xs={1} md={2} lg={3} className="g-4">
                        <AnimatePresence mode="wait">
                            {postList.map((post, index) => {
                                const isNew = Date.now() - new Date(post.json.created_at).getTime() < 24 * 60 * 60 * 1000;
                                return (
                                    <Col key={post.json.id}>
                                        <motion.div
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            variants={cardVariants}
                                            transition={{ ...cardTransition, delay: index * 0.05 }}
                                            className="h-100"
                                        >
                                            <div
                                                className="crypto-news-card cursor-pointer"
                                                onClick={() => handleCardClick(post.json)}
                                                style={{ minHeight: '180px' }}
                                            >
                                                <div className="crypto-news-title line-clamp-5">
                                                    {post.json.title}
                                                </div>
                                                <div className="crypto-news-date mt-auto">
                                                    <FaRegClock style={{ color: '#94a3b8' }} />
                                                    <span>{post.json.created_at?.substring(0, 10)}</span>
                                                    {isNew && (
                                                        <span className="crypto-news-badge-new">NEW</span>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    </Col>
                                );
                            })}
                        </AnimatePresence>
                    </Row>
                </motion.div>
            )}

            {!loading && postList.length === 0 && (
                <div className="text-center my-5 p-5 bg-light rounded shadow-sm">
                    <h4 className="text-muted">조회된 가상화폐 뉴스가 없습니다.</h4>
                    <p className="text-secondary">검색 조건을 다시 확인해 주세요.</p>
                </div>
            )}

            {/* 페이지 네비게이션 */}
            {!loading && totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                    <Pagination>
                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />

                        {/* 페이지 번호 렌더링 (최대 5개) */}
                        {[...Array(totalPages)].map((_, i) => {
                            const pageNum = i + 1;
                            const startPage = Math.max(1, currentPage - 2);
                            const endPage = Math.min(totalPages, startPage + 4);

                            if (pageNum >= startPage && pageNum <= endPage) {
                                  return (
                                      <Pagination.Item
                                          key={pageNum}
                                          active={pageNum === currentPage}
                                          onClick={() => handlePageChange(pageNum)}
                                      >
                                          {pageNum}
                                      </Pagination.Item>
                                  );
                            }
                            return null;
                        })}

                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                    </Pagination>
                </div>
            )}
        </Container>
    );
};

export { CryptoNotice };